import React, { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/store'

interface ProfileDropdownProps {
  onLogout: () => void
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ onLogout }) => {
  const { user, mode, isTelegramMode } = useAuth()
  
  // Функция для получения инициалов пользователя
  const getUserInitials = () => {
    const firstName = user?.first_name || ''
    const lastName = user?.last_name || ''
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase()
    } else if (firstName) {
      return firstName.slice(0, 2).toUpperCase()
    }
    
    // Если нет first_name/last_name, используем username или email
    const fallback = (user?.username || user?.email || '').trim()
    if (fallback) {
      const base = fallback.split('@')[0]
      return base.slice(0, 2).toUpperCase()
    }
    
    return '👤'
  }

  const fullName = (() => {
    const firstName = user?.first_name || ''
    const lastName = user?.last_name || ''
    const nameField = (user as any)?.name as string | undefined
    const fallback = nameField || user?.username || user?.email || 'Пользователь'
    return (firstName || lastName) ? `${firstName} ${lastName}`.trim() : fallback
  })()

  // Функция для сокращения имени в зависимости от размера экрана
  const getDisplayName = (breakpoint: 'desktop' | 'tablet' | 'mobile') => {
    // Приоритет: first_name + last_name → name → username/email
    const firstName = user?.first_name || ''
    const lastName = user?.last_name || ''
    const nameField = (user as any)?.name as string | undefined
    const fallbackName = nameField || user?.username || user?.email || 'Пользователь'
    
    // Если есть first_name, используем его
    if (firstName) {
      switch (breakpoint) {
        case 'desktop':
          return `${firstName} ${lastName}`.trim()
        case 'tablet':
          return `${firstName} ${lastName ? lastName[0] + '.' : ''}`.trim()
        case 'mobile':
          return getUserInitials()
        default:
          return firstName
      }
    }
    
    // Если нет first_name, используем name/username/email
    switch (breakpoint) {
      case 'desktop':
        return fallbackName
      case 'tablet':
        // Для планшета сокращаем длинные имена
        return fallbackName.length > 15 ? fallbackName.split(' ')[0] : fallbackName
      case 'mobile':
        return getUserInitials()
      default:
        return fallbackName
    }
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          {/* Кнопка профиля - адаптивная */}
          <Menu.Button aria-label="Открыть меню профиля" title={fullName} className="group flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/80 hover:bg-white backdrop-blur-sm border border-gray-200/50 hover:border-gray-300/50 shadow-sm hover:shadow-md transition-all duration-200 ease-out">
            {/* Аватар */}
            <div className="relative">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                {getUserInitials()}
              </div>
              {/* Индикатор онлайн */}
              <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 border-2 border-white rounded-full shadow-sm"></div>
            </div>
            
            {/* Имя пользователя - скрывается на мобильных */}
            <div className="hidden sm:flex flex-col items-start min-w-0">
              {/* Десктоп версия */}
              <span className="hidden lg:block text-sm font-medium text-gray-900 truncate max-w-[96px]" title={fullName}>
                {getDisplayName('desktop')}
              </span>
              {/* Планшет версия */}
              <span className="hidden sm:block lg:hidden text-sm font-medium text-gray-900 truncate max-w-[72px]" title={fullName}>
                {getDisplayName('tablet')}
              </span>
              {/* Режим работы убран из шапки для компактности */}
            </div>
            
            {/* Стрелка */}
            <svg 
              className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </Menu.Button>

          {/* Выпадающее меню - компактное */}
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-40 origin-top-right bg-white/95 backdrop-blur-lg rounded-xl shadow-xl border border-gray-200/50 focus:outline-none overflow-hidden z-50">
              {/* Пункты меню — без повторения имени/режима */}
              <div className="py-1">
                {/* Документы */}
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to={isTelegramMode ? "/miniapp/documents" : "/documents"}
                      className={`${active ? 'bg-green-50 text-green-700' : 'text-gray-700'} flex items-center gap-2 px-2 py-1.5 text-sm transition-colors duration-150`}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center ${active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="font-medium">Документы</span>
                    </Link>
                  )}
                </Menu.Item>

                {/* Настройки */}
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to={isTelegramMode ? "/miniapp/profile" : "/profile"}
                      className={`${active ? 'bg-blue-50 text-blue-700' : 'text-gray-700'} flex items-center gap-2 px-2 py-1.5 text-sm transition-colors duration-150`}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center ${active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <span className="font-medium">Настройки</span>
                    </Link>
                  )}
                </Menu.Item>

                {/* Разделитель и выход - только для браузерного режима */}
                {!isTelegramMode && (
                  <>
                    <div className="my-1 border-t border-gray-100"></div>

                    {/* Выход */}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={onLogout}
                          className={`${active ? 'bg-red-50 text-red-700' : 'text-gray-700'} flex items-center gap-2 px-2 py-1.5 text-sm transition-colors duration-150 w-full text-left`}
                        >
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center ${active ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l-4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                          </div>
                          <span className="font-medium">Выход</span>
                        </button>
                      )}
                    </Menu.Item>
                  </>
                )}

                {/* Информационная строка режима (опционально) */}
                <div className="mt-1 border-t border-gray-100 px-2 py-1.5">
                  <div className="flex items-center gap-1 text-[11px] text-gray-500">
                    <span>{mode === 'telegram' ? '📱' : '🌐'}</span>
                    <span className="truncate">Режим: {mode === 'telegram' ? 'Telegram' : 'Браузер'}</span>
                  </div>
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  )
}
